import React from "react";
import { Seo } from "../../shared/Seo";
import styles from "./LegalPage.module.scss";

const SITE_URL = "https://achtungroyale.io";

// Short, plain-language privacy policy. Kept deliberately minimal for now —
// revisit once there's an online backend / real user base. Not legal advice.
const LAST_UPDATED = "October 29, 2026";
const CONTACT_EMAIL = "hello@achtungroyale.io";

export default function PrivacyPage() {
  return (
    <>
      <Seo
        title="Privacy Policy | Achtung Royale"
        description="Privacy Policy for Achtung Royale. We don't sell your data. Learn what we collect, who processes it, and how to delete your account."
        canonical={`${SITE_URL}/privacy`}
      />
      <div className={styles.legal}>
        <p className={styles.updated}>Last updated: {LAST_UPDATED}</p>

        <p>
          Achtung Royale is a free online game. In short: we don't sell your data. Here's what
          the game touches.
        </p>

        <h2>What we collect</h2>
        <ul>
          <li>
            <strong>Only if you sign in:</strong> an account managed by Supabase Auth. We receive
            your email address and, if the provider supplies them, your display name and avatar.
          </li>
          <li>
            <strong>On your device only:</strong> your settings, in-game name, and local match
            stats are stored in your browser and are never sent to us.
          </li>
          <li>
            <strong>Only if you buy something:</strong> payments are handled by Stripe; we
            never see your card number. We keep a record of what you bought (item, price,
            date, and Stripe's reference for the payment) to deliver your purchase and
            handle any refunds or disputes.
          </li>
          <li>
            <strong>Online games:</strong> for each online game we keep the mode, when it was
            played, and who took part (your account if you are signed in, otherwise a scrambled
            code for your browser, with no name, email or IP address), so we can count how many
            people play.
          </li>
          <li>
            <strong>Error reports:</strong> when something breaks in the game, a report of what
            broke (the error, the game version, and the kind of device and browser) is sent to
            Sentry so we can fix it. It carries no name, email, or IP address.
          </li>
          <li>
            <strong>Ads:</strong> the Results page after a match shows one ad from Google
            AdSense. Google may use cookies to pick and measure ads, and in the EU and UK it
            asks for your consent first. You can change your ad choices in Google's{" "}
            <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">ad settings</a>, and{" "}
            <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">this page</a>{" "}
            explains how Google uses data from sites that show its ads.
          </li>
          <li>
            <strong>Standard logs:</strong> our hosting and auth providers may log routine request
            data (such as IP address) to keep the service running and secure.
          </li>
        </ul>

        <h2>Who processes it</h2>
        <p>
          We use Supabase (sign-in and game data), Cloudflare (hosting), Stripe
          (payments, only if you buy something), Sentry (error reports), Google AdSense (the ad on the Results page), and Plausible (analytics). Plausible
          is cookieless and EU-hosted: it records aggregate visits and a few gameplay
          events, builds no profile of you, and cannot follow you to other sites. Signing in with Google, Discord, or
          Facebook is also covered by that provider's own privacy policy.
        </p>

        <h2>Your choices</h2>
        <p>
          You can remove the on-device data anytime by clearing your browser data for this site.
          To delete your account, email us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <h2>Children</h2>
        <p>
          The game isn't directed at children under 13, and we don't knowingly collect their data.
        </p>

        <h2>Changes &amp; contact</h2>
        <p>
          We may update this policy; the date above shows the latest version. Questions? Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </div>
    </>
  );
}
